import type { SpherePotionId, SphereSpellId } from './sim/sphereDuel';

export type SphereSequencedAction =
  | { kind: 'cast'; spell: SphereSpellId }
  | { kind: 'potion'; potion: SpherePotionId }
  | { kind: 'cancel' };

export interface PendingSphereCommand {
  matchId: number;
  sequence: number;
  action: SphereSequencedAction;
}

/**
 * Client-side correlation only. This predicts presentation, never game state.
 * Authority can replace the next sequence on any state anchor (notably reconnect).
 */
export class SphereCommandTracker {
  private matchId?: number;
  private nextSequence = 1;
  private readonly pending = new Map<number, PendingSphereCommand>();

  sync(matchId: number, nextSequence = 1): boolean {
    const replaced = this.matchId !== matchId;
    if (replaced) {
      this.pending.clear();
      this.matchId = matchId;
    }
    this.nextSequence = Math.max(this.nextSequence, nextSequence);
    return replaced;
  }

  allocate(action: SphereSequencedAction): PendingSphereCommand | undefined {
    if (this.matchId === undefined) return undefined;
    const command = {
      matchId: this.matchId,
      sequence: this.nextSequence++,
      action,
    };
    this.pending.set(command.sequence, command);
    return command;
  }

  settle(matchId: number, sequence: number): PendingSphereCommand | undefined {
    if (matchId !== this.matchId) return undefined;
    const command = this.pending.get(sequence);
    this.pending.delete(sequence);
    return command;
  }

  reset(): void {
    this.matchId = undefined;
    this.nextSequence = 1;
    this.pending.clear();
  }

  get pendingCount(): number {
    return this.pending.size;
  }
}
